import { Box, Code, Grid, GridItem, HStack, Heading, Link, Spinner, Text, VStack, Badge } from "@chakra-ui/react"
import { useParams, Link as RouterLink } from "react-router-dom"
import { type ColumnDef } from "@tanstack/react-table"
import SectionCard from "../../framework/SectionCard"
import ErrorAlert from "../../components/ErrorAlert"
import DataTable from "../../framework/DataTable"
import { formatDateTime } from "../../utils/datetime"
import { useUserDetail, useUserLogs, type UserLog } from "../../hooks/useUsers"
import { useAuthToken } from "../../hooks/useAuthToken"
import { API_BASE_URL } from "../../framework/api"

type UserInstall = {
  id: number
  createdAt: string
  installId: string
  platform: string
  model: string
  version: string
}

const installColumns: ColumnDef<UserInstall>[] = [
  {
    accessorKey: "createdAt",
    header: "Created",
    id: "createdAt",
    cell: ({ getValue }) => formatDateTime(getValue<string>()),
  },
  {
    accessorKey: "installId",
    header: "Install ID",
    id: "installId",
    cell: ({ getValue }) => <Code fontSize="xs">{getValue<string>()}</Code>,
  },
  {
    accessorKey: "platform",
    header: "Platform",
    id: "platform",
  },
  {
    accessorKey: "model",
    header: "Model",
    id: "model",
    cell: ({ getValue }) => getValue<string>() || "—",
  },
  {
    accessorKey: "version",
    header: "Version",
    id: "version",
    cell: ({ getValue }) => getValue<string>() || "—",
  },
  {
    accessorKey: "id",
    header: "DB ID",
    id: "id",
    cell: ({ getValue }) => <Code fontSize="xs">{getValue<number>()}</Code>,
  },
]

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  let amount = value
  let unitIdx = 0
  while (amount >= 1024 && unitIdx < units.length - 1) {
    amount /= 1024
    unitIdx++
  }
  return `${amount.toFixed(unitIdx === 0 ? 0 : 1)} ${units[unitIdx]}`
}

async function downloadProcessedLog(log: UserLog, token: string) {
  try {
    const url = `${API_BASE_URL}${log.processedDownloadUrl}`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to download log: ${response.statusText}`)
    }
    
    // Get the filename from Content-Disposition header, or use fallback
    const disposition = response.headers.get('Content-Disposition')
    let filename = `${(log.profileName || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_')}_logs.json`
    if (disposition) {
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }
    
    const blob = await response.blob()
    const url2 = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url2
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url2)
  } catch (error) {
    console.error('Failed to download log:', error)
    alert(`Failed to download log: ${error instanceof Error ? error.message : String(error)}`)
  }
}

function createLogColumns(token: string): ColumnDef<UserLog>[] {
  return [
    {
      accessorKey: "createdAt",
      header: "Uploaded",
      id: "createdAt",
      cell: ({ getValue }) => formatDateTime(getValue<string>()),
    },
    {
      accessorKey: "entryCount",
      header: "Entries",
      id: "entryCount",
      cell: ({ getValue }) => getValue<number>().toLocaleString(),
    },
    {
      accessorKey: "compressedSizeBytes",
      header: "Size (gz)",
      id: "compressedSizeBytes",
      cell: ({ row }) => formatBytes(row.original.compressedSizeBytes),
    },
    {
      accessorKey: "platform",
      header: "Platform",
      id: "platform",
      cell: ({ getValue }) => getValue<string | null>() || "—",
    },
    {
      accessorKey: "appVersion",
      header: "Version",
      id: "appVersion",
      cell: ({ getValue }) => getValue<string | null>() || "—",
    },
    {
      accessorKey: "installId",
      header: "Install ID",
      id: "installId",
      cell: ({ getValue }) => {
        const value = getValue<string | null>()
        return value ? <Code fontSize="xs">{value}</Code> : "—"
      },
    },
    {
      accessorKey: "downloadUrl",
      header: "Download",
      id: "downloadUrl",
      cell: ({ row }) => (
        <Link
          onClick={async (e) => {
            e.preventDefault()
            await downloadProcessedLog(row.original, token)
          }}
          color="blue.400"
          cursor="pointer"
        >
          Download JSON
        </Link>
      ),
    },
  ]
}

export default function UserDetail() {
  const { uid } = useParams<{ uid: string }>()
  const token = useAuthToken()
  const { data: user, isLoading, error } = useUserDetail(uid!)
  const { data: userLogs = [], isLoading: logsLoading, error: logsError } = useUserLogs(uid)

  const logColumns = createLogColumns(token)

  if (isLoading) {
    return (
      <VStack gap="4" py="8">
        <Spinner size="xl" />
        <Text color="gray.400">Loading user...</Text>
      </VStack>
    )
  }

  if (error) {
    return (
      <Box>
        <ErrorAlert message={(error as Error)?.message ?? "Unable to load user"} />
      </Box>
    )
  }

  if (!user) {
    return (
      <Box>
        <ErrorAlert message="User not found" />
      </Box>
    )
  }

  return (
    <VStack gap="6" align="stretch">
      <HStack justify="space-between">
        <HStack gap="3">
          <RouterLink to="/users">
            <Link color="blue.400">
              ← Users
            </Link>
          </RouterLink>
          <Heading size="lg">{user.email}</Heading>
          {user.hasActiveSubscription && <Badge colorPalette="green">Subscribed</Badge>}
        </HStack>
      </HStack>

      <SectionCard>
        <Heading size="md" mb="4">
          User Information
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="4">
          <GridItem>
            <VStack align="stretch" gap="3">
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Database ID
                </Text>
                <Code fontSize="sm">{user.databaseId}</Code>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  UID
                </Text>
                <Code fontSize="sm">{user.uid}</Code>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Email
                </Text>
                <Text>{user.email}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Profile Name
                </Text>
                <Text>{user.profileName || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  IDP Name
                </Text>
                <Text>{user.idpName || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  External ID
                </Text>
                <Code fontSize="sm">{user.externalId || "—"}</Code>
              </Box>
            </VStack>
          </GridItem>

          <GridItem>
            <VStack align="stretch" gap="3">
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Identity Provider
                </Text>
                <Badge colorPalette={user.idProvider === "reviewer" ? "purple" : "blue"}>
                  {user.idProvider}
                </Badge>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Created
                </Text>
                <Text>{formatDateTime(user.createdAt)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Updated
                </Text>
                <Text>{formatDateTime(user.updatedAt)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Last Login
                </Text>
                <Text>{formatDateTime(user.lastLoginAt)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Trial Sessions Used
                </Text>
                <Text>{user.trialSessionsUsed}</Text>
              </Box>
            </VStack>
          </GridItem>
        </Grid>

        {user.hasActiveSubscription && (
          <Box mt="6" pt="6" borderTopWidth="1px" borderColor="gray.700">
            <Heading size="sm" mb="3">
              Subscription Details
            </Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="4">
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Product ID
                </Text>
                <Code fontSize="sm">{user.subscriptionProductId || "—"}</Code>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  Expires At
                </Text>
                <Text>{user.subscriptionExpiresAt ? formatDateTime(user.subscriptionExpiresAt) : "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.400" mb="1">
                  RevenueCat Customer ID
                </Text>
                <Code fontSize="sm">{user.revenueCatCustomerId || "—"}</Code>
              </Box>
              {user.promoCode && (
                <>
                  <Box>
                    <Text fontSize="sm" color="gray.400" mb="1">
                      Promo Code Used
                    </Text>
                    <HStack gap="2">
                      <Badge colorPalette="purple">{user.promoCode}</Badge>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.400" mb="1">
                      Promo Code Used At
                    </Text>
                    <Text>{user.promoCodeUsedAt ? formatDateTime(user.promoCodeUsedAt) : "—"}</Text>
                  </Box>
                </>
              )}
            </Grid>
          </Box>
        )}
      </SectionCard>

      <SectionCard>
        <Heading size="md" mb="4">
          User Installs ({user.installs.length})
        </Heading>
        {user.installs.length > 0 ? (
          <Box overflowX="auto">
            <DataTable
              columns={installColumns}
              data={user.installs}
              emptyMessage="No installs"
            />
          </Box>
        ) : (
          <Text color="gray.400">No installs recorded</Text>
        )}
      </SectionCard>

      <SectionCard>
        <Heading size="md" mb="4">
          Uploaded Logs ({userLogs.length})
        </Heading>
        {logsLoading ? (
          <HStack gap="3">
            <Spinner size="sm" />
            <Text color="gray.400">Loading logs...</Text>
          </HStack>
        ) : logsError ? (
          <ErrorAlert message={(logsError as Error)?.message ?? "Unable to load logs"} />
        ) : userLogs.length > 0 ? (
          <Box overflowX="auto">
            <DataTable
              columns={logColumns}
              data={userLogs}
              emptyMessage="No uploaded logs"
            />
          </Box>
        ) : (
          <Text color="gray.400">No uploaded logs</Text>
        )}
      </SectionCard>
    </VStack>
  )
}
